import { observer } from 'mobx-react-lite';
import React from 'react';

import { Button, Divider, Tooltip, useSmartPosition } from '@flixbus/honeycomb-react';

import { authStore } from 'auth/authStore';
import { useOutsideClick } from 'hooks/useClickOutside';
import { ACTION_MARKERS, ASSIGNMENT_STATUSES } from 'pages/RideDetails/components/Assigment/const';
import { ASSIGNMENT_LABEL_TEXT } from 'pages/RideDetails/components/Assigment/text';
import { rideDetailsStore } from 'pages/RideDetails/RideDetailsStore';
import { isIndiaOnlyUser } from 'utils/auth';

import styles from './styles.module.css';

const ACTION_MAP = {
	created: [
		[
			{
				status: ASSIGNMENT_STATUSES.CONFIRMED,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.confirmAssignment,
			},
			{
				status: ASSIGNMENT_STATUSES.ACTUAL,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.toActual,
			},
		],
		[
			{
				status: ASSIGNMENT_STATUSES.REJECTED,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.unassign,
				extraStyle: styles.unassign,
			},
		],
		[
			{
				status: ASSIGNMENT_STATUSES.PENDING,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.sendNotification,
				inactive: true,
			},
		],
	],
	pending: [
		[
			{
				status: ASSIGNMENT_STATUSES.CONFIRMED,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.confirmAssignment,
			},
			{
				status: ASSIGNMENT_STATUSES.ACTUAL,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.toActual,
			},
		],
		[
			{
				status: ASSIGNMENT_STATUSES.REJECTED,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.unassign,
				extraStyle: styles.unassign,
			},
		],
		[
			{
				status: ASSIGNMENT_STATUSES.PENDING,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.sendNotification,
				inactive: true,
			},
		],
	],
	confirmed: [
		[
			{
				status: ASSIGNMENT_STATUSES.ACTUAL,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.toActual,
			},
		],
		[
			{
				status: ASSIGNMENT_STATUSES.REJECTED,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.unassign,
				extraStyle: styles.unassign,
			},
		],
	],
	rejected: [
		[
			{
				status: ACTION_MARKERS.REASSIGN,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.reassign,
			},
			{
				status: ACTION_MARKERS.ASSIGN_ANOTHER,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.assignAnother,
			},
		],
		[
			{
				status: ACTION_MARKERS.ASSIGN_TO_FULL,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.assignFull,
				inactive: true,
			},
		],
		[
			{
				status: ASSIGNMENT_STATUSES.UNMERGE,
				label: ASSIGNMENT_LABEL_TEXT.actionLabelMenuItems.unmergeAndClean,
			},
		],
	],
};

const AssignmentActionMenu = observer(
	({ id, status, container, children, triggerRef, isFirst, isLast, isTag }) => {
		const targetInnerRef = React.useRef();
		const balloonInnerRef = React.useRef();
		const [isActive, setIsActive] = React.useState(false);
		const [smartPosition, smartAlignment] = useSmartPosition({
			targetRef: triggerRef,
			elementRef: balloonInnerRef,
			smartPositionRef: container,
		});

		const ref = useOutsideClick(() => setIsActive(false));

		const isIndiaUser = isIndiaOnlyUser(authStore.roles);

		const handleStatusChange = (id, action) => {
			if (action.status === ASSIGNMENT_STATUSES.UNMERGE) {
				rideDetailsStore.breakRejectedBatch(id);
				return;
			}
			rideDetailsStore.updateAssignmentStatus(id, action.status);
		};

		const renderActions = () => {
			const groups = ACTION_MAP[status];
			if (!groups) return null;

			return groups.map((group, groupIndex) => (
				<React.Fragment key={`group-${groupIndex}`}>
					{group.map((action) => {
						if (isIndiaUser && action.status === ASSIGNMENT_STATUSES.ACTUAL) return null;

						return (
							<Button
								appearance="link"
								disabled={action.inactive}
								key={action.status}
								onClick={() => handleStatusChange(id, action)}
								extraClasses={`${Object.hasOwn(action, 'extraStyle') ? action.extraStyle : ''} ${styles.btn}`}
								data-testid={`assignment-action-${action.status}`}
							>
								{action.label}
							</Button>
						);
					})}

					{groupIndex < groups.length - 1 && <Divider />}
				</React.Fragment>
			));
		};

		const actions = (
			<div ref={ref} className={styles.tooltip}>
				{renderActions()}
			</div>
		);

		const placement = {
			alignment: '',
			position: '',
		};
		if (isTag) {
			placement.position = 'top';
		}
		if (isFirst) {
			placement.alignment = 'start';
		}
		if (isFirst && !isTag) {
			placement.position = 'right';
			placement.alignment = 'end';
		}
		if (isLast) {
			placement.position = 'left';
			placement.alignment = 'end';
		}
		if (isFirst && isLast && isTag) {
			placement.position = 'top';
			placement.alignment = 'start';
		}

		return (
			<Tooltip
				id="tooltip-def"
				content={actions}
				size="content-fit"
				alignment={placement.alignment ? placement.alignment : smartAlignment}
				position={placement.position ? placement.position : smartPosition}
				innerRef={targetInnerRef}
				balloonRef={balloonInnerRef}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setIsActive(!isActive);
				}}
				extraClasses={styles.onTop}
				active={isActive}
			>
				{children}
			</Tooltip>
		);
	}
);

export default AssignmentActionMenu;
